import { Box, BoxProps, Flex, Heading, TextProps } from '@chakra-ui/react'
import { useModalAction } from '@components/common/modal/modal.context'
import ArrowIcon from '@components/icons/arrow-icon'
import Carousel from '@components/ui/carousel/carousel'
import { SwiperSlide } from '@components/ui/carousel/slider'
import { useStore } from '@contexts/store'
import { Product } from '@framework/model-types/Product'
import useWindowSize from '@hooks/use-window-size'
import cn from 'classnames'
import Link from 'next/link'
import { RefObject } from 'react'
import { breakpoints } from '../../utils/constants/breakpoints'
import { ProductCardShimmer as ProductCardLoader } from '../ShimmerEffects/ProductCard'
import { productCards } from './component-options'

interface ProductsCarouselProps {
  sectionHeading: string
  categorySlug?: string
  className?: string
  products?: Product[]
  loading: boolean
  error?: string
  limit?: number
  uniqueKey?: string
  carouselBreakpoint?: {} | any
  pageTopRef?: RefObject<HTMLDivElement>
  boxStyleProps?: BoxProps
  headingStyleProps?: TextProps
}

const ProductsCarousel: React.FC<ProductsCarouselProps> = ({
  sectionHeading,
  categorySlug,
  className = 'mb-8 lg:mb-10 xl:mb-12',
  products,
  loading,
  error,
  limit,
  uniqueKey,
  carouselBreakpoint,
  pageTopRef,
  boxStyleProps,
  headingStyleProps
}) => {
  const { width } = useWindowSize()
  const { closeModal } = useModalAction()
  const handleProductClick = () => {
    pageTopRef && pageTopRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const { storeTemplate } = useStore()

  const ThemeProductCard = productCards[storeTemplate]

  return (
    <Box
      className={cn(
        'max-w-[1920px] overflow-hidden 4xl:overflow-visible px-4 md:px-6 lg:px-8 2xl:ps-10 2xl:pe-0 4xl:pe-10 mx-auto relative',
        className,
      )}
      {...boxStyleProps}
    >
      <Flex className="flex flex-wrap items-center justify-between mb-5 md:mb-6">
        {/* <Box className="-mt-1.5 mb-0"> */}
        <Heading
          className="text-brand-dark text-lg lg:text-xl xl:text-[22px] xl:leading-8 font-bold"
          fontSize="1.3em"
          {...headingStyleProps}
        >
          {sectionHeading}
        </Heading>
        {/* </Box> */}
      </Flex>

      <div className="xl:-me-40 2xl:-me-28 4xl:me-0 relative after-item-opacity">
        <Carousel
          breakpoints={carouselBreakpoint || breakpoints}
          className="-mx-1.5 md:-mx-2 xl:-mx-2.5 -mt-4"
        >
          {loading && !products?.length ? (
            Array.from({ length: limit! }).map((_, idx) => (
              <SwiperSlide
                key={`${uniqueKey}-${idx}`}
                className="px-1.5 md:px-2 xl:px-2.5 py-4"
              >
                <ProductCardLoader />
              </SwiperSlide>
            ))
          ) : (
            <>
              {products?.map((product, idx) => (
                <SwiperSlide
                  key={`${uniqueKey}-${idx}`}
                  className="px-1.5 md:px-2 xl:px-2.5 py-4"
                >
                  <Box onClick={handleProductClick}>
                    <ThemeProductCard product={product} />
                  </Box>
                </SwiperSlide>
              ))}
              <SwiperSlide className="p-2.5 flex items-center justify-center">
                <Flex color="primary.700" align="center">
                  <Link
                    onClick={closeModal}
                    href={
                      categorySlug ? `/search?${categorySlug}` : null ?? '/#'
                    }
                    className={`p-4 flex items-center justify-center flex-col hover:opacity-80`}
                  >
                    <ArrowIcon />
                    <span className="font-semibold text-sm sm:text-base text-skin-primary block pt-1.5 sm:pt-3.5">
                      See All
                    </span>
                  </Link>
                </Flex>
              </SwiperSlide>
              {width! > 1024 && width! < 1921 && <SwiperSlide />}
            </>
          )}
        </Carousel>
      </div>
    </Box>
  )
}

export default ProductsCarousel
